import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { onValue, push, ref } from 'firebase/database';
import { db, isFirebaseConfigured, signInStatsViewer } from './lib/firebase';

const GRID_SIZE = 6;
const PICK_SECONDS = 30;
const QUESTION_SECONDS = 40;
const ALLOWED_TYPES = [
  'rose',
  'lion',
  'falcon',
  'fire',
  'clap',
  'rocket',
  'thunder',
  'trophy',
  'sword',
  'star',
  'heart',
  'crown',
  'gem'
];
const REACTIONS = [
  { type: 'rose', emoji: '🌹', label: 'وردة' },
  { type: 'lion', emoji: '🦁', label: 'أسد' },
  { type: 'falcon', emoji: '🦅', label: 'صقر' },
  { type: 'fire', emoji: '🔥', label: 'نار' },
  { type: 'clap', emoji: '👏', label: 'تصفيق' },
  { type: 'rocket', emoji: '🚀', label: 'صاروخ' },
  { type: 'thunder', emoji: '⚡', label: 'رعد' },
  { type: 'trophy', emoji: '🏆', label: 'كأس' },
  { type: 'sword', emoji: '⚔️', label: 'سيف' },
  { type: 'star', emoji: '🌟', label: 'نجمة' },
  { type: 'heart', emoji: '❤️', label: 'قلب' },
  { type: 'crown', emoji: '👑', label: 'تاج' },
  { type: 'gem', emoji: '💎', label: 'ماسة' }
];
const OPTION_LABELS = ['أ', 'ب', 'ج', 'د'];
const TEXT = {
  ar: {
    dir: 'rtl',
    liveTitle: 'مشاهدة مباشرة',
    activeRooms: 'المباريات المباشرة',
    noRooms: 'لا توجد مباريات مباشرة الآن.',
    turn: 'الدور',
    cheerTeam: 'شجّع فريقك',
    cheerCount: 'تشجيع',
    latestCheers: 'آخر التشجيعات',
    waitingAnswer: 'بانتظار إجابة اللاعب...',
    questionFor: 'سؤال لـ:',
    level: 'المستوى:',
    correct: 'إجابة صحيحة',
    wrong: 'إجابة خاطئة',
    correctAnswer: 'الإجابة الصحيحة',
    scoringBadge: 'وضع عد المربعات',
    scoringHelp: 'تم قطع المسارات على الفريقين. الفائز عند اكتمال اللوحة هو صاحب المربعات الأكثر.',
    draw: 'تعادل',
    finalScore: 'النتيجة',
    gameEndedTitle: 'انتهت المسابقة',
    gameEndedWinner: 'الفائز',
    gameEndedDraw: 'انتهت بالتعادل',
    gameEndedMessage: 'تم إنهاء هذه الغرفة',
    closePage: 'إغلاق الصفحة',
    autoCloseIn: 'سيتم إغلاق الصفحة خلال'
  },
  en: {
    dir: 'ltr',
    liveTitle: 'Live Watch',
    activeRooms: 'Live games',
    noRooms: 'No live games right now.',
    turn: 'Turn',
    cheerTeam: 'Cheer for your team',
    cheerCount: 'Cheers',
    latestCheers: 'Latest cheers',
    waitingAnswer: 'Waiting for the player answer...',
    questionFor: 'Question for:',
    level: 'Level:',
    correct: 'Correct answer',
    wrong: 'Wrong answer',
    correctAnswer: 'Correct answer',
    scoringBadge: 'Square count mode',
    scoringHelp: 'Both teams have blocked each other paths. When the board is full, the team with more squares wins.',
    draw: 'Draw',
    finalScore: 'Score',
    gameEndedTitle: 'Game ended',
    gameEndedWinner: 'Winner',
    gameEndedDraw: 'Ended in a draw',
    gameEndedMessage: 'This room has ended',
    closePage: 'Close page',
    autoCloseIn: 'This page will close in'
  }
};

function getLanguage() {
  return `${navigator.language || ''}`.toLowerCase().startsWith('ar') ? 'ar' : 'en';
}

function emptyGrid() {
  return Array.from({ length: GRID_SIZE }, () => Array(GRID_SIZE).fill(0));
}

function normalizeGrid(grid) {
  const rows = Array.isArray(grid) ? grid : Object.values(grid || {});
  if (!rows.length) return emptyGrid();
  return Array.from({ length: GRID_SIZE }, (_, r) => {
    const row = rows[r];
    const cells = Array.isArray(row) ? row : Object.values(row || {});
    return Array.from({ length: GRID_SIZE }, (_, c) => Number(cells[c] || 0));
  });
}

function getCellNum(r, c, dir = 'rtl') {
  if (dir === 'ltr') return r * GRID_SIZE + c + 1;
  return r * GRID_SIZE + (GRID_SIZE - c);
}

function getQuestionPayload(currentQuestion) {
  if (!currentQuestion?.question) return null;
  try {
    const parsed =
      typeof currentQuestion.question === 'string'
        ? JSON.parse(currentQuestion.question)
        : currentQuestion.question;
    return {
      ...currentQuestion,
      parsed
    };
  } catch (error) {
    console.warn('question parse failed:', error);
    return null;
  }
}

function getQuestionSecondsLeft(questionPayload) {
  if (!questionPayload?.timestamp) return QUESTION_SECONDS;
  const elapsed = Math.floor((Date.now() - questionPayload.timestamp) / 1000);
  return Math.max(0, QUESTION_SECONDS - elapsed);
}

function createParticle(emoji, team) {
  return {
    key: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
    emoji,
    team,
    x: 8 + Math.random() * 84,
    drift: -38 + Math.random() * 76,
    size: 28 + Math.random() * 24,
    duration: 1500 + Math.random() * 900
  };
}

export default function RoomSpectatorPage({ roomCode }) {
  const [room, setRoom] = useState(null);
  const [liveRooms, setLiveRooms] = useState([]);
  const [selectedRoomCode, setSelectedRoomCode] = useState(roomCode || '');
  const [cheers, setCheers] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState('red');
  const [particles, setParticles] = useState([]);
  const [secondsLeft, setSecondsLeft] = useState(PICK_SECONDS);
  const [questionSecondsLeft, setQuestionSecondsLeft] = useState(QUESTION_SECONDS);
  const [visibleQuestion, setVisibleQuestion] = useState(null);
  const [visibleAnswerResult, setVisibleAnswerResult] = useState(null);
  const [endCloseSeconds, setEndCloseSeconds] = useState(10);
  const [error, setError] = useState('');
  const [uid, setUid] = useState('');
  const seenCheersRef = useRef(new Set());
  const activeRoomCode = (roomCode || selectedRoomCode || '').toUpperCase();
  const text = TEXT[room?.question_language || getLanguage()] || TEXT.ar;
  const grid = normalizeGrid(room?.grid);
  const team1Name = room?.team1_name || 'الفريق الأول';
  const team2Name = room?.team2_name || 'الفريق الثاني';
  const activeTeamName = room?.current_turn === 2 ? team2Name : team1Name;
  const currentQuestion = room?.current_question || null;
  const currentQuestionTimestamp = currentQuestion?.timestamp || 0;
  const currentQuestionValue = currentQuestion?.question || null;
  const currentQuestionRow = currentQuestion?.r ?? null;
  const currentQuestionCol = currentQuestion?.c ?? null;
  const currentQuestionOpenedBy = currentQuestion?.opened_by || '';
  const questionPayload = useMemo(
    () =>
      getQuestionPayload({
        timestamp: currentQuestionTimestamp,
        question: currentQuestionValue,
        r: currentQuestionRow,
        c: currentQuestionCol,
        opened_by: currentQuestionOpenedBy
      }),
    [currentQuestionCol, currentQuestionOpenedBy, currentQuestionRow, currentQuestionTimestamp, currentQuestionValue]
  );
  const questionKey = questionPayload
    ? `${questionPayload.timestamp || 0}-${questionPayload.r}-${questionPayload.c}-${questionPayload.opened_by || ''}`
    : '';
  const answerResult = room?.current_answer_result || null;
  const answerTimestamp = answerResult?.timestamp || 0;
  const answerSelectedIndex = answerResult?.selected_index ?? null;
  const answerIsCorrect = Boolean(answerResult?.is_correct);
  const answerBy = answerResult?.answered_by || '';
  const questionClosedAt = room?.current_question_closed_at || 0;
  const gameEnded = Boolean(room?.winner || room?.status === 'finished' || room?.status === 'ended');
  const finalWinnerName = room?.winner === 1 ? team1Name : room?.winner === 2 ? team2Name : '';
  const closeRoomPage = useCallback(() => {
    window.close();
    window.setTimeout(() => {
      if (!window.closed) {
        window.location.href = '/';
      }
    }, 150);
  }, []);

  useEffect(() => {
    if (!isFirebaseConfigured) return undefined;
    let active = true;

    signInStatsViewer()
      .then((signedUid) => {
        if (!active) return;
        setUid(signedUid || '');
      })
      .catch((authError) => {
        setError(`تعذر دخول المشاهدة: ${authError.code || authError.message}`);
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!isFirebaseConfigured || roomCode) return undefined;

    return onValue(
      ref(db, 'rooms'),
      (snapshot) => {
        const data = snapshot.val() || {};
        const list = Object.entries(data)
          .map(([code, value]) => {
            const { cheers: ignoredCheers, ...roomData } = value || {};
            return { ...roomData, code: roomData.code || code };
          })
          .filter((item) => item.status === 'waiting' || item.status === 'playing')
          .sort((a, b) => (b.turn_started_at || b.created_at || 0) - (a.turn_started_at || a.created_at || 0));

        setLiveRooms(list);
        setError('');
        setSelectedRoomCode((current) => {
          if (current && list.some((item) => item.code === current)) return current;
          return list[0]?.code || '';
        });
      },
      (firebaseError) => {
        setError(`تعذر تحميل المباريات: ${firebaseError.code || firebaseError.message}`);
      }
    );
  }, [roomCode]);

  useEffect(() => {
    if (!isFirebaseConfigured || !activeRoomCode) {
      setRoom(null);
      setCheers([]);
      return undefined;
    }

    seenCheersRef.current = new Set();
    setParticles([]);
    const unsubscribeRoom = onValue(
      ref(db, `rooms/${activeRoomCode}`),
      (snapshot) => {
        const value = snapshot.val();
        if (!value) {
          setError(roomCode ? 'الغرفة غير موجودة أو انتهت المباراة.' : TEXT[getLanguage()].noRooms);
          setRoom(null);
          return;
        }
        const { cheers: ignoredCheers, ...roomData } = value;
        setRoom({ ...roomData, code: roomData.code || activeRoomCode });
        setError('');
      },
      (firebaseError) => {
        setError(`تعذر تحميل الغرفة: ${firebaseError.code || firebaseError.message}`);
      }
    );

    const unsubscribeCheers = onValue(
      ref(db, `rooms/${activeRoomCode}/cheers`),
      (snapshot) => {
        const data = snapshot.val() || {};
        const list = Object.entries(data)
          .map(([key, value]) => ({ key, ...value }))
          .sort((a, b) => (a.created_at || a.timestamp || 0) - (b.created_at || b.timestamp || 0));
        setCheers(list);

        list.slice(-12).forEach((cheer) => {
          if (seenCheersRef.current.has(cheer.key)) return;
          seenCheersRef.current.add(cheer.key);
          const emoji = cheer.emoji || REACTIONS.find((reaction) => reaction.type === cheer.type)?.emoji || '🌟';
          setParticles((old) => [...old, ...Array.from({ length: 7 }, () => createParticle(emoji, cheer.team))]);
        });
      },
      (firebaseError) => {
        console.warn('cheers read failed:', firebaseError);
      }
    );

    return () => {
      unsubscribeRoom?.();
      unsubscribeCheers?.();
    };
  }, [activeRoomCode, roomCode]);

  useEffect(() => {
    if (!room) return undefined;
    const tick = () => {
      const startedAt = room.turn_started_at || room.created_at || Date.now();
      const elapsed = Math.floor((Date.now() - startedAt) / 1000);
      setSecondsLeft(Math.max(0, PICK_SECONDS - elapsed));
    };
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [room]);

  useEffect(() => {
    if (!questionPayload) return;
    setVisibleQuestion(questionPayload);
    setVisibleAnswerResult(null);
  }, [questionKey, questionPayload]);

  useEffect(() => {
    if (!answerTimestamp) return;
    setVisibleAnswerResult({
      is_correct: answerIsCorrect,
      selected_index: answerSelectedIndex,
      answered_by: answerBy,
      timestamp: answerTimestamp
    });
  }, [answerBy, answerIsCorrect, answerSelectedIndex, answerTimestamp]);

  useEffect(() => {
    if (!visibleAnswerResult) return undefined;
    const timer = setTimeout(() => {
      setVisibleQuestion(null);
      setVisibleAnswerResult(null);
    }, 3000);
    return () => clearTimeout(timer);
  }, [visibleAnswerResult]);

  useEffect(() => {
    if (questionPayload) return undefined;
    if (!visibleQuestion) return undefined;

    if (questionClosedAt) {
      const ownerValue = visibleQuestion.opened_by === 'team2' ? 2 : 1;
      const cellValue = grid?.[visibleQuestion.r]?.[visibleQuestion.c] || 0;
      const inferredIsCorrect = cellValue === ownerValue;
      if (!visibleAnswerResult || (visibleAnswerResult.inferred && visibleAnswerResult.is_correct !== inferredIsCorrect)) {
        setVisibleAnswerResult({
          is_correct: inferredIsCorrect,
          selected_index: null,
          timestamp: questionClosedAt,
          inferred: true
        });
      }
    }

    const closeDelay = questionClosedAt ? (visibleAnswerResult ? 2200 : 900) : visibleAnswerResult ? 2600 : 350;
    const timer = setTimeout(() => {
      setVisibleQuestion(null);
      setVisibleAnswerResult(null);
    }, closeDelay);
    return () => clearTimeout(timer);
  }, [grid, questionClosedAt, questionPayload, visibleAnswerResult, visibleQuestion]);

  useEffect(() => {
    if (!visibleQuestion) return undefined;
    const tick = () => setQuestionSecondsLeft(getQuestionSecondsLeft(visibleQuestion));
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [visibleQuestion]);

  useEffect(() => {
    if (!gameEnded) {
      setEndCloseSeconds(10);
      return undefined;
    }

    setVisibleQuestion(null);
    setVisibleAnswerResult(null);
    setEndCloseSeconds(10);

    const startedAt = Date.now();
    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startedAt) / 1000);
      setEndCloseSeconds(Math.max(0, 10 - elapsed));
    }, 250);
    const closeTimer = setTimeout(closeRoomPage, 10000);

    return () => {
      clearInterval(interval);
      clearTimeout(closeTimer);
    };
  }, [closeRoomPage, gameEnded]);

  const removeParticle = useCallback((key) => {
    setParticles((old) => old.filter((particle) => particle.key !== key));
  }, []);

  const cheerCounts = useMemo(() => {
    return cheers.reduce(
      (acc, cheer) => {
        if (cheer.team === 'blue') acc.blue += 1;
        else acc.red += 1;
        return acc;
      },
      { red: 0, blue: 0 }
    );
  }, [cheers]);
  const latestCheers = cheers.slice(-8).reverse();

  const sendCheer = async (reaction) => {
    if (!activeRoomCode || !uid || !ALLOWED_TYPES.includes(reaction.type)) return;
    const now = Date.now();
    try {
      await push(ref(db, `rooms/${activeRoomCode}/cheers`), {
        uid,
        type: reaction.type,
        created_at: now,
        emoji: reaction.emoji,
        team: selectedTeam,
        timestamp: now
      });
    } catch (firebaseError) {
      setError(`تعذر إرسال التشجيع: ${firebaseError.code || firebaseError.message}`);
    }
  };

  return (
    <main dir={text.dir} style={styles.page}>
      <div style={styles.particleLayer} aria-hidden="true">
        {particles.map((particle) => (
          <FlyingEmoji key={particle.key} particle={particle} onDone={removeParticle} />
        ))}
      </div>

      <section style={styles.shell}>
        <header style={styles.header}>
          <div>
            <p style={styles.eyebrow}>{text.liveTitle}</p>
            <h1 style={styles.title}>⚔️ MindClash 🌐</h1>
          </div>
          <div style={styles.roomPill}>{activeRoomCode || 'LIVE'}</div>
        </header>

        {error ? <div style={styles.error}>{error}</div> : null}

        {!roomCode && liveRooms.length > 0 ? (
          <section style={styles.liveRooms}>
            <strong>{text.activeRooms}</strong>
            <div style={styles.liveRoomList}>
              {liveRooms.slice(0, 6).map((item) => (
                <button
                  key={item.code}
                  type="button"
                  onClick={() => setSelectedRoomCode(item.code)}
                  style={{
                    ...styles.liveRoomButton,
                    ...(item.code === activeRoomCode ? styles.liveRoomButtonActive : {})
                  }}
                >
                  <span>{item.code}</span>
                  <small>{item.team1_name || '-'} vs {item.team2_name || '-'}</small>
                </button>
              ))}
            </div>
          </section>
        ) : null}
        {!roomCode && liveRooms.length === 0 && !error ? (
          <section style={styles.emptyLive}>{text.noRooms}</section>
        ) : null}

        <section style={styles.scoreHeader}>
          <TeamBadge name={team1Name} color="red" active={room?.current_turn === 1} />
          <div style={styles.vs}>VS</div>
          <TeamBadge name={team2Name} color="blue" active={room?.current_turn === 2} />
        </section>

        <section style={styles.turnCard}>
          <span>{text.turn}: {activeTeamName}</span>
          <strong>⏱ {secondsLeft.toLocaleString(text.dir === 'rtl' ? 'ar' : 'en')}</strong>
        </section>

        <ReadOnlyBoard grid={grid} dir={text.dir} />

        {room?.scoring_mode ? (
          <section style={styles.scoringBanner}>
            <strong>{text.scoringBadge}</strong>
            <span>{text.scoringHelp}</span>
          </section>
        ) : null}

        {room?.winner ? (
          <section style={styles.finalResultBanner}>
            <strong>{room.winner === 3 ? text.draw : room.winner === 2 ? team2Name : team1Name}</strong>
            {room.final_score ? (
              <span>
                {text.finalScore}: {room.final_score.team1} - {room.final_score.team2}
              </span>
            ) : null}
          </section>
        ) : null}

        <section style={styles.cheerPanel}>
          <h2 style={styles.sectionTitle}>{text.cheerTeam}</h2>
          <div style={styles.teamPicker}>
            <button
              type="button"
              onClick={() => setSelectedTeam('red')}
              style={{ ...styles.pickButton, ...(selectedTeam === 'red' ? styles.redActive : {}) }}
            >
              🔴 {team1Name}
            </button>
            <button
              type="button"
              onClick={() => setSelectedTeam('blue')}
              style={{ ...styles.pickButton, ...(selectedTeam === 'blue' ? styles.blueActive : {}) }}
            >
              🔵 {team2Name}
            </button>
          </div>

          <div style={styles.reactions}>
            {REACTIONS.map((reaction) => (
              <button
                key={reaction.type}
                type="button"
                title={reaction.label}
                onClick={() => sendCheer(reaction)}
                style={styles.reaction}
              >
                {reaction.emoji}
              </button>
            ))}
          </div>

          <div style={styles.cheerScore}>
            <span>🔴 {text.cheerCount}: {cheerCounts.red.toLocaleString(text.dir === 'rtl' ? 'ar' : 'en')}</span>
            <span>🔵 {text.cheerCount}: {cheerCounts.blue.toLocaleString(text.dir === 'rtl' ? 'ar' : 'en')}</span>
          </div>

          {latestCheers.length > 0 ? (
            <div style={styles.latestCheers}>
              <strong>{text.latestCheers}</strong>
              <div style={styles.latestCheerList}>
                {latestCheers.map((cheer) => {
                  const reaction = REACTIONS.find((item) => item.type === cheer.type);
                  const teamName = cheer.team === 'blue' ? team2Name : team1Name;
                  return (
                    <span
                      key={cheer.key}
                      style={{
                        ...styles.latestCheer,
                        ...(cheer.team === 'blue' ? styles.latestCheerBlue : styles.latestCheerRed)
                      }}
                    >
                      <b>{cheer.emoji || reaction?.emoji || '🌟'}</b>
                      {teamName}
                    </span>
                  );
                })}
              </div>
            </div>
          ) : null}
        </section>
      </section>

      {visibleQuestion ? (
        <SpectatorQuestionModal
          payload={visibleQuestion}
          answerResult={visibleAnswerResult}
          teamName={visibleQuestion.opened_by === 'team2' ? team2Name : team1Name}
          teamColor={visibleQuestion.opened_by === 'team2' ? 'blue' : 'red'}
          secondsLeft={questionSecondsLeft}
          text={text}
        />
      ) : null}

      {gameEnded ? (
        <GameEndedModal
          text={text}
          winnerName={finalWinnerName}
          isDraw={room?.winner === 3}
          finalScore={room?.final_score}
          secondsLeft={endCloseSeconds}
          onClose={closeRoomPage}
        />
      ) : null}
    </main>
  );
}

function GameEndedModal({ text, winnerName, isDraw, finalScore, secondsLeft, onClose }) {
  const locale = text.dir === 'rtl' ? 'ar' : 'en';

  return (
    <div style={styles.endOverlay}>
      <section style={styles.endCard}>
        <div style={styles.endIcon}>🏁</div>
        <h2 style={styles.endTitle}>{text.gameEndedTitle}</h2>
        <p style={styles.endWinner}>
          {isDraw ? text.gameEndedDraw : winnerName ? `${text.gameEndedWinner}: ${winnerName}` : text.gameEndedMessage}
        </p>
        {finalScore ? (
          <div style={styles.endScore}>
            {text.finalScore}: {finalScore.team1} - {finalScore.team2}
          </div>
        ) : null}
        <div style={styles.endCountdown}>
          {text.autoCloseIn} {secondsLeft.toLocaleString(locale)}
        </div>
        <button type="button" onClick={onClose} style={styles.endCloseButton}>
          {text.closePage}
        </button>
      </section>
    </div>
  );
}

function TeamBadge({ name, color, active }) {
  return (
    <div
      style={{
        ...styles.teamBadge,
        ...(color === 'red' ? styles.redBadge : styles.blueBadge),
        ...(active ? styles.activeBadge : {})
      }}
    >
      <span>{color === 'red' ? '🔴' : '🔵'}</span>
      <strong>{name}</strong>
    </div>
  );
}

function ReadOnlyBoard({ grid, dir }) {
  return (
    <section style={styles.boardWrap}>
      <div style={{ ...styles.board, direction: dir }}>
        {grid.flatMap((row, r) => {
          const cells = row.map((value, c) => ({ value, c }));
          return (dir === 'rtl' ? [...cells].reverse() : cells)
            .map(({ value, c }) => (
              <div
                key={`${r}-${c}`}
                style={{
                  ...styles.cell,
                  ...(value === 1 ? styles.redCell : value === 2 ? styles.blueCell : styles.emptyCell)
                }}
              >
                {getCellNum(r, c, dir)}
              </div>
            ));
        })}
      </div>
    </section>
  );
}

function SpectatorQuestionModal({ payload, answerResult, teamName, teamColor, secondsLeft, text }) {
  const question = payload.parsed;
  const selectedIndex = answerResult?.selected_index ?? null;
  const showAnswer = Boolean(answerResult);
  const isCorrect = Boolean(answerResult?.is_correct);
  const accent = teamColor === 'blue' ? '#2f6fe6' : '#e23b3b';

  if (showAnswer) {
    return (
      <div style={styles.modalBackdrop}>
        <section
          style={{
            ...styles.resultOnlyCard,
            ...(isCorrect ? styles.resultOnlyCorrect : styles.resultOnlyWrong)
          }}
        >
          <div style={styles.resultOnlyGlow} />
          <div style={styles.resultOnlyIcon}>{isCorrect ? '✓' : '✕'}</div>
          <h2 style={styles.resultOnlyTitle}>{isCorrect ? text.correct : text.wrong}</h2>
          <p style={styles.resultOnlyTeam}>{teamName}</p>
          <div style={styles.resultOnlyDivider} />
          <div style={styles.resultOnlyLine}>
            <span>{isCorrect ? '🏆' : '⚡'}</span>
            <strong>
              {isCorrect
                ? question.options[question.answer]
                : `${text.correctAnswer}: ${question.options[question.answer]}`}
            </strong>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div style={styles.modalBackdrop}>
      <section style={styles.modalCard}>
        <header style={styles.modalHeader}>
          <div style={{ ...styles.timer, ...(secondsLeft <= 5 ? styles.timerWarn : {}) }}>
            {secondsLeft.toLocaleString(text.dir === 'rtl' ? 'ar' : 'en')}
          </div>
          <div style={{ ...styles.questionTeam, borderColor: accent, color: accent }}>
            <span style={{ ...styles.teamDot, background: accent }} />
            {text.questionFor} {teamName}
          </div>
        </header>

        <div style={styles.progressTrack}>
          <div
            style={{
              ...styles.progressFill,
              width: `${Math.max(0, Math.min(100, (secondsLeft / QUESTION_SECONDS) * 100))}%`,
              background: secondsLeft <= 5 ? '#dc2626' : '#0f5f48'
            }}
          />
        </div>

        <div style={styles.questionMeta}>
          {question.category ? <span style={styles.chip}>{question.category}</span> : null}
          {question.difficulty ? <span style={styles.chip}>{text.level} {question.difficulty}</span> : null}
        </div>

        <h2 style={styles.questionText}>{question.question}</h2>

        <div style={styles.options}>
          {question.options.map((option, idx) => {
            const correct = showAnswer && idx === question.answer;
            const wrong = showAnswer && selectedIndex === idx && selectedIndex !== question.answer;
            return (
              <div
                key={`${option}-${idx}`}
                style={{
                  ...styles.option,
                  ...(correct ? styles.optionCorrect : {}),
                  ...(wrong ? styles.optionWrong : {})
                }}
              >
                <span style={styles.optionLabel}>{OPTION_LABELS[idx]}</span>
                <span style={styles.optionText}>{option}</span>
                {correct ? <strong style={styles.resultMark}>✓</strong> : null}
                {wrong ? <strong style={styles.resultMark}>✕</strong> : null}
              </div>
            );
          })}
        </div>

        {showAnswer ? (
          <div style={{ ...styles.resultBanner, ...(isCorrect ? styles.resultCorrect : styles.resultWrong) }}>
            {isCorrect ? text.correct : text.wrong}
          </div>
        ) : (
          <div style={styles.waitingAnswer}>{text.waitingAnswer}</div>
        )}
      </section>
    </div>
  );
}

function FlyingEmoji({ particle, onDone }) {
  useEffect(() => {
    const timer = setTimeout(() => onDone(particle.key), particle.duration + 80);
    return () => clearTimeout(timer);
  }, [onDone, particle.duration, particle.key]);

  return (
    <span
      style={{
        ...styles.particle,
        '--x': `${particle.x}vw`,
        '--drift': `${particle.drift}px`,
        '--dur': `${particle.duration}ms`,
        fontSize: particle.size,
        color: particle.team === 'blue' ? '#2f6fe6' : '#e23b3b'
      }}
    >
      {particle.emoji}
    </span>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background:
      'radial-gradient(circle at 20% 10%, rgba(215,173,74,.18), transparent 30%), linear-gradient(180deg, #07372d 0%, #0f5f48 44%, #f7ecd2 100%)',
    fontFamily: 'Tajawal, system-ui, sans-serif',
    color: '#0b3d31',
    padding: 16,
    overflow: 'hidden'
  },
  shell: {
    width: 'min(620px, 100%)',
    margin: '0 auto',
    display: 'grid',
    gap: 12,
    position: 'relative',
    zIndex: 2
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    color: '#fff8eb',
    paddingTop: 12
  },
  eyebrow: { margin: 0, color: '#f0dca3', fontWeight: 900 },
  title: { margin: '3px 0 0', fontSize: 25, fontWeight: 900 },
  roomPill: {
    background: 'rgba(255,255,255,.15)',
    border: '1px solid rgba(255,255,255,.32)',
    color: '#f0dca3',
    borderRadius: 999,
    padding: '8px 12px',
    fontWeight: 900,
    letterSpacing: 2
  },
  scoreHeader: {
    display: 'grid',
    gridTemplateColumns: '1fr auto 1fr',
    alignItems: 'center',
    gap: 8
  },
  teamBadge: {
    borderRadius: 16,
    padding: '10px 12px',
    display: 'flex',
    gap: 7,
    alignItems: 'center',
    justifyContent: 'center',
    background: '#fff8eb',
    border: '2px solid transparent',
    boxShadow: '0 10px 22px rgba(7,42,34,.14)'
  },
  redBadge: { borderColor: 'rgba(226,59,59,.26)' },
  blueBadge: { borderColor: 'rgba(47,111,230,.26)' },
  activeBadge: { outline: '3px solid #d7ad4a' },
  vs: { color: '#f0dca3', fontWeight: 900 },
  turnCard: {
    minHeight: 46,
    borderRadius: 16,
    background: '#fff8eb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 16px',
    fontWeight: 900,
    boxShadow: '0 10px 22px rgba(7,42,34,.14)'
  },
  boardWrap: {
    background: 'rgba(255,248,235,.86)',
    border: '1px solid rgba(255,255,255,.6)',
    borderRadius: 22,
    padding: 12,
    boxShadow: '0 18px 34px rgba(7,42,34,.18)'
  },
  board: {
    direction: 'rtl',
    display: 'grid',
    gridTemplateColumns: 'repeat(6, 1fr)',
    gap: 8
  },
  cell: {
    aspectRatio: '1 / 1',
    borderRadius: 12,
    display: 'grid',
    placeItems: 'center',
    fontWeight: 900,
    fontSize: 'clamp(15px, 5vw, 27px)'
  },
  emptyCell: {
    background: '#ecdcbb',
    border: '1px solid #b89f6f',
    color: 'rgba(18,53,44,.48)'
  },
  redCell: { background: '#e23b3b', color: '#fff' },
  blueCell: { background: '#2f6fe6', color: '#fff' },
  scoringBanner: {
    background: 'rgba(215,173,74,.22)',
    border: '1.5px solid #d7ad4a',
    borderRadius: 18,
    padding: 12,
    display: 'grid',
    gap: 4,
    textAlign: 'center',
    color: '#12352c',
    fontWeight: 900
  },
  finalResultBanner: {
    background: '#fff8eb',
    border: '1.5px solid #d7ad4a',
    borderRadius: 18,
    padding: 12,
    display: 'grid',
    gap: 4,
    textAlign: 'center',
    color: '#12352c',
    fontWeight: 900
  },
  endOverlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 8,
    display: 'grid',
    placeItems: 'center',
    padding: 18,
    background: 'rgba(7,55,45,.86)',
    backdropFilter: 'blur(5px)'
  },
  endCard: {
    width: 'min(420px, 100%)',
    borderRadius: 24,
    padding: '28px 22px',
    display: 'grid',
    gap: 12,
    justifyItems: 'center',
    textAlign: 'center',
    background: '#fff8eb',
    border: '2px solid #d7ad4a',
    color: '#12352c',
    boxShadow: '0 28px 70px rgba(0,0,0,.34)'
  },
  endIcon: {
    width: 76,
    height: 76,
    borderRadius: '50%',
    display: 'grid',
    placeItems: 'center',
    background: '#0f5f48',
    color: '#fff',
    fontSize: 36,
    boxShadow: '0 14px 26px rgba(7,42,34,.22)'
  },
  endTitle: {
    margin: 0,
    fontSize: 'clamp(28px, 8vw, 40px)',
    fontWeight: 900
  },
  endWinner: {
    margin: 0,
    fontSize: 21,
    fontWeight: 900,
    color: '#0f5f48'
  },
  endScore: {
    borderRadius: 999,
    padding: '9px 16px',
    background: 'rgba(215,173,74,.2)',
    border: '1px solid rgba(215,173,74,.55)',
    fontWeight: 900
  },
  endCountdown: {
    color: 'rgba(18,53,44,.75)',
    fontWeight: 900
  },
  endCloseButton: {
    width: '100%',
    border: 0,
    borderRadius: 16,
    padding: '13px 18px',
    background: '#0f5f48',
    color: '#fff8eb',
    fontFamily: 'Tajawal, system-ui, sans-serif',
    fontSize: 17,
    fontWeight: 900,
    cursor: 'pointer'
  },
  cheerPanel: {
    background: '#fff8eb',
    borderRadius: 22,
    padding: 14,
    boxShadow: '0 18px 34px rgba(7,42,34,.18)'
  },
  sectionTitle: { margin: '0 0 10px', fontSize: 18, fontWeight: 900 },
  teamPicker: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 },
  pickButton: {
    border: '2px solid #ead7a5',
    borderRadius: 14,
    background: '#fff',
    padding: '10px 8px',
    fontWeight: 900,
    color: '#0b3d31'
  },
  redActive: { borderColor: '#e23b3b', background: '#fde2e2' },
  blueActive: { borderColor: '#2f6fe6', background: '#dbeafe' },
  reactions: {
    display: 'grid',
    gridTemplateColumns: 'repeat(6, 1fr)',
    gap: 8,
    marginTop: 12
  },
  reaction: {
    aspectRatio: '1 / 1',
    borderRadius: 14,
    border: '1px solid #ead7a5',
    background: '#fff',
    fontSize: 25
  },
  cheerScore: {
    display: 'flex',
    justifyContent: 'space-around',
    gap: 10,
    marginTop: 12,
    fontWeight: 900
  },
  latestCheers: {
    marginTop: 12,
    display: 'grid',
    gap: 8,
    color: '#0b3d31',
    fontWeight: 900
  },
  latestCheerList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8
  },
  latestCheer: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    borderRadius: 999,
    padding: '7px 10px',
    border: '1.5px solid #ead7a5',
    background: '#fff',
    fontSize: 13
  },
  latestCheerRed: {
    borderColor: '#f3a3a3',
    background: '#fff1f1'
  },
  latestCheerBlue: {
    borderColor: '#98b7f4',
    background: '#eef5ff'
  },
  error: {
    background: '#fee2e2',
    color: '#991b1b',
    borderRadius: 14,
    padding: 12,
    fontWeight: 900,
    textAlign: 'center'
  },
  liveRooms: {
    background: '#fff8eb',
    borderRadius: 18,
    padding: 12,
    display: 'grid',
    gap: 10,
    boxShadow: '0 10px 22px rgba(7,42,34,.14)',
    fontWeight: 900
  },
  liveRoomList: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
    gap: 8
  },
  liveRoomButton: {
    border: '1.5px solid #ead7a5',
    borderRadius: 14,
    background: '#fff',
    color: '#0b3d31',
    padding: '9px 10px',
    display: 'grid',
    gap: 3,
    fontWeight: 900,
    textAlign: 'center'
  },
  liveRoomButtonActive: {
    borderColor: '#0f5f48',
    background: '#dcf5e6'
  },
  emptyLive: {
    background: '#fff8eb',
    borderRadius: 18,
    padding: 16,
    color: '#0b3d31',
    fontWeight: 900,
    textAlign: 'center'
  },
  modalBackdrop: {
    position: 'fixed',
    inset: 0,
    zIndex: 5,
    display: 'grid',
    placeItems: 'center',
    padding: 16,
    background: 'rgba(11,61,49,.78)',
    backdropFilter: 'blur(3px)'
  },
  modalCard: {
    width: 'min(100%, 520px)',
    background: '#fff8eb',
    border: '2px solid #f0dca3',
    borderRadius: 24,
    padding: 18,
    boxShadow: '0 28px 80px rgba(0,0,0,.28)'
  },
  resultOnlyCard: {
    position: 'relative',
    width: 'min(520px, calc(100vw - 32px))',
    minHeight: 360,
    overflow: 'hidden',
    borderRadius: 30,
    padding: '34px 24px',
    display: 'grid',
    placeItems: 'center',
    gap: 12,
    textAlign: 'center',
    border: '3px solid transparent',
    boxShadow: '0 28px 60px rgba(0,0,0,.34)'
  },
  resultOnlyCorrect: {
    background: 'linear-gradient(180deg, #ecfdf5, #bbf7d0)',
    borderColor: '#22c55e',
    color: '#14532d'
  },
  resultOnlyWrong: {
    background: 'linear-gradient(180deg, #fff1f2, #fecaca)',
    borderColor: '#ef4444',
    color: '#991b1b'
  },
  resultOnlyGlow: {
    position: 'absolute',
    inset: -90,
    background: 'radial-gradient(circle, rgba(255,255,255,.9), rgba(255,255,255,0) 58%)',
    pointerEvents: 'none'
  },
  resultOnlyIcon: {
    position: 'relative',
    width: 116,
    height: 116,
    borderRadius: '50%',
    display: 'grid',
    placeItems: 'center',
    background: '#fff',
    fontSize: 72,
    fontWeight: 900,
    boxShadow: '0 18px 34px rgba(7,42,34,.18)'
  },
  resultOnlyTitle: {
    position: 'relative',
    margin: 0,
    fontSize: 'clamp(30px, 8vw, 48px)',
    fontWeight: 900
  },
  resultOnlyTeam: {
    position: 'relative',
    margin: 0,
    fontSize: 22,
    fontWeight: 900
  },
  resultOnlyDivider: {
    position: 'relative',
    width: 'min(300px, 74%)',
    height: 2,
    borderRadius: 999,
    background: 'rgba(18,53,44,.2)'
  },
  resultOnlyLine: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    borderRadius: 999,
    padding: '10px 16px',
    background: 'rgba(255,255,255,.76)',
    color: '#12352c',
    fontWeight: 900
  },
  modalHeader: {
    display: 'flex',
    flexDirection: 'row-reverse',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10
  },
  timer: {
    width: 54,
    height: 54,
    borderRadius: 27,
    display: 'grid',
    placeItems: 'center',
    background: '#0b3d31',
    border: '3px solid #d7ad4a',
    color: '#fff',
    fontWeight: 900,
    fontSize: 22
  },
  timerWarn: { background: '#b21d1d', borderColor: '#fca5a5' },
  questionTeam: {
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    border: '1.5px solid',
    borderRadius: 999,
    padding: '9px 13px',
    background: '#fff',
    fontWeight: 900
  },
  teamDot: { width: 10, height: 10, borderRadius: 99 },
  progressTrack: {
    height: 7,
    borderRadius: 999,
    overflow: 'hidden',
    background: 'rgba(18,53,44,.12)',
    marginTop: 14
  },
  progressFill: { height: '100%', transition: 'width .25s linear' },
  questionMeta: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 6,
    marginTop: 12
  },
  chip: {
    borderRadius: 999,
    padding: '6px 10px',
    background: 'rgba(15,95,72,.1)',
    color: '#0f5f48',
    fontSize: 12,
    fontWeight: 900
  },
  questionText: {
    margin: '18px 0',
    color: '#12352c',
    textAlign: 'center',
    fontSize: 'clamp(20px, 5vw, 27px)',
    lineHeight: 1.55,
    fontWeight: 900
  },
  resultHero: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    borderRadius: 20,
    padding: '14px 16px',
    margin: '10px 0 14px',
    border: '2px solid transparent',
    boxShadow: '0 16px 28px rgba(7,42,34,.14)'
  },
  resultHeroCorrect: {
    background: '#dcf5e6',
    borderColor: '#22c55e',
    color: '#14532d'
  },
  resultHeroWrong: {
    background: '#fee2e2',
    borderColor: '#ef4444',
    color: '#991b1b'
  },
  resultHeroIcon: {
    width: 48,
    height: 48,
    borderRadius: '50%',
    display: 'grid',
    placeItems: 'center',
    background: '#fff',
    fontSize: 32,
    fontWeight: 900
  },
  options: { display: 'grid', gap: 9 },
  option: {
    minHeight: 58,
    display: 'flex',
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 10,
    borderRadius: 18,
    padding: '10px 12px',
    background: '#fff',
    border: '1.5px solid #e7d9b4',
    transition: 'background .2s, border-color .2s'
  },
  optionCorrect: { background: '#dcf5e6', borderColor: '#34a853' },
  optionWrong: { background: '#fde2e2', borderColor: '#dc2626' },
  optionLabel: {
    width: 34,
    height: 34,
    borderRadius: 17,
    display: 'grid',
    placeItems: 'center',
    background: '#0b3d31',
    color: '#fff',
    fontWeight: 900
  },
  optionText: { flex: 1, textAlign: 'right', color: '#12352c', fontWeight: 900, fontSize: 17 },
  resultMark: { color: '#12352c', fontSize: 24, fontWeight: 900 },
  resultBanner: {
    marginTop: 14,
    borderRadius: 18,
    padding: '10px 14px',
    textAlign: 'center',
    fontSize: 20,
    fontWeight: 900
  },
  resultCorrect: { color: '#14532d', background: '#dcf5e6' },
  resultWrong: { color: '#991b1b', background: '#fee2e2' },
  waitingAnswer: {
    marginTop: 14,
    color: '#6c6658',
    textAlign: 'center',
    fontWeight: 900
  },
  particleLayer: {
    position: 'fixed',
    inset: 0,
    pointerEvents: 'none',
    zIndex: 1,
    overflow: 'hidden'
  },
  particle: {
    position: 'absolute',
    left: 'var(--x)',
    bottom: 20,
    animation: 'floatCheer var(--dur) ease-out forwards',
    filter: 'drop-shadow(0 8px 12px rgba(0,0,0,.22))'
  }
};
